import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { adminAPI } from '../services/api';

const EMPTY_FORM = {
  name: '',
  country: '',
  amount: '',
  deadline: '',
  url: '',
  is_active: true,
};

const ScholarshipsAdminPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // for edit route
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);

  // Auth check + auto-open form if on edit route
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const hasToken   = localStorage.getItem('adminToken');
    if (!isLoggedIn || !hasToken) {
      navigate('/admin');
      return;
    }

    // If navigated to /admin/scholarships/edit/:id, open form with that id
    if (id) {
      setEditingId(id);
      setShowForm(true);
    }

    fetchScholarships();
  }, [navigate, id]);

 const fetchScholarships = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getScholarships();
      setScholarships(data);

      // If editing, pre-fill form with existing data
      if (id && Array.isArray(data)) {
        const existing = data.find(s => s.id === id);
        if (existing) {
          setForm({
            name:      existing.name,
            country:   existing.country,
            amount:    existing.amount,
            deadline:  existing.deadline,
            url:       existing.url,
            is_active: existing.is_active,
          });
        }
      }
    } catch (error) {
      if (error.message === 'Authentication required' || error.message?.includes('401')) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminUsername');
        navigate('/admin');
        return;
      }
      toast.error('Failed to load scholarships');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.country.trim() || !form.amount.trim() || !form.deadline.trim() || !form.url.trim()) {
      toast.error('All fields are required');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await adminAPI.updateScholarship(editingId, form);
        toast.success('Scholarship updated successfully');
      } else {
        await adminAPI.createScholarship(form);
        toast.success('Scholarship created successfully');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchScholarships();
      navigate('/admin/dashboard');
    } catch (error) {
      if (error.message === 'Authentication required' || error.message?.includes('401')) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminUsername');
        navigate('/admin');
        return;
      }
      toast.error(error.message || 'Failed to save scholarship');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (s) => {
    setForm({
      name:      s.name,
      country:   s.country,
      amount:    s.amount,
      deadline:  s.deadline,
      url:       s.url,
      is_active: s.is_active,
    });
    setEditingId(s.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scholarship?')) return;
    try {
      await adminAPI.deleteScholarship(id);
      toast.success('Scholarship deleted');
      fetchScholarships();
    } catch (error) {
      toast.error('Failed to delete scholarship');
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
    navigate('/admin/dashboard');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">

      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-medicaps-blue">
            Higher Studies Scholarships
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage scholarships shown on the public Higher Studies page
          </p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-medicaps-blue hover:bg-medicaps-blue/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Scholarship
          </Button>
        )}
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow border p-6 mb-8">
          <h2 className="text-lg font-semibold text-medicaps-blue mb-4">
            {editingId ? 'Edit Scholarship' : 'Add New Scholarship'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Scholarship Name *</label>
              <input
                name="name" value={form.name} onChange={handleChange} required
                placeholder="e.g. Fulbright Scholarship"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
              <input
                name="country" value={form.country} onChange={handleChange} required
                placeholder="e.g. USA"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
              <input
                name="amount" value={form.amount} onChange={handleChange} required
                placeholder="e.g. Full tuition + living"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deadline *</label>
              <input
                name="deadline" value={form.deadline} onChange={handleChange} required
                placeholder="e.g. 15/10/2025"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Visit URL *</label>
              <input
                name="url" value={form.url} onChange={handleChange} required type="url"
                placeholder="https://fulbright.state.gov"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-2">
              <input
                type="checkbox" name="is_active" id="is_active"
                checked={form.is_active} onChange={handleChange}
                className="h-4 w-4 accent-medicaps-blue"
              />
              <label htmlFor="is_active" className="text-sm text-slate-700">
                Active (visible on public page)
              </label>
            </div>

            <div className="md:col-span-2 flex gap-3">
              <Button type="submit" disabled={saving} className="bg-medicaps-blue hover:bg-medicaps-blue/90">
                {saving ? 'Saving...' : editingId ? 'Update Scholarship' : 'Create Scholarship'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>

          </form>
        </div>
      )}

      {/* Scholarships Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-medicaps-blue" />
        </div>
      ) : scholarships.length === 0 ? (
        <div className="text-center py-20 text-slate-400 bg-white rounded-xl border">
          No scholarships yet. Click "Add Scholarship" to create one.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Name</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Country</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Amount</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Deadline</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Status</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scholarships.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 transition">
                  <td className="px-6 py-4 font-medium text-slate-800">{s.name}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline">{s.country}</Badge>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{s.amount}</td>
                  <td className="px-6 py-4 text-slate-600">{s.deadline}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      s.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {s.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => handleEdit(s)} className="text-medicaps-blue hover:opacity-70 transition">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:opacity-70 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScholarshipsAdminPage;